import { Head, Link } from "@inertiajs/react"
import { Inbox as InboxIcon } from "lucide-react"
import { useState } from "react"

import { InboxReview } from "@/components/products/work_orders/inbox-review"
import type {
  AttachTargets,
  InboxDocument,
} from "@/components/products/work_orders/inbox-types"
import { formatDate } from "@/components/products/work_orders/status-chip"
import type { WorkOrderRow } from "@/components/products/work_orders/types"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface Props {
  documents: InboxDocument[]
  work_orders: WorkOrderRow[]
  attach_targets: AttachTargets
  counts: { documents: number; work_orders: number }
  can_edit: boolean
}

/**
 * Everything the email pipeline extracted that nobody has accepted yet.
 *
 * Master-detail rather than a stack of cards: reviewing is a rhythm — read the
 * document, check three fields, confirm, next — and that rhythm dies if the
 * page scrolls between every decision.
 */
export default function InboxIndex({
  documents,
  work_orders,
  attach_targets,
  counts,
  can_edit,
}: Props) {
  const routes = useOrgRoutes()
  const [selectedId, setSelectedId] = useState<number | null>(
    documents[0]?.id ?? null,
  )

  const selected =
    documents.find((doc) => doc.id === selectedId) ?? documents[0] ?? null

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Inbox", href: routes.inboxPath() },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Inbox" />

      <div className="space-y-5 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Inbox</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Documents the pipeline read but nobody has accepted yet. Nothing
            here has changed a work order.
          </p>
        </div>

        {counts.documents === 0 && counts.work_orders === 0 ? (
          <Card>
            <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-16 text-sm">
              <InboxIcon className="size-8 opacity-40" />
              Nothing is waiting on review.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_1fr]">
            <div className="space-y-2">
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => setSelectedId(doc.id)}
                  className={cn(
                    "hover:border-primary/50 w-full rounded-lg border p-3 text-left transition-colors",
                    selected?.id === doc.id && "border-primary bg-accent/40",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium">
                      {doc.car_number ?? doc.filename ?? doc.doc_type_label}
                    </span>
                    {doc.verification.status === "fail" && (
                      <Badge variant="destructive">Failed</Badge>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Badge variant="outline">{doc.doc_type_label}</Badge>
                    <span className="text-muted-foreground text-xs">
                      {formatDate(doc.created_at)}
                    </span>
                  </div>
                  {/* A create proposal is PLACED — it opens a new work order on
                      confirm. Only a document the pipeline could place nowhere
                      needs an operator to file it. */}
                  {doc.proposal.work_order_id == null &&
                    (doc.proposal.create_type != null ? (
                      <p className="text-muted-foreground mt-1.5 text-xs">
                        Opens a new {doc.proposal.create_type_label ?? "work order"}
                      </p>
                    ) : (
                      <p className="text-muted-foreground mt-1.5 text-xs">
                        Needs filing
                      </p>
                    ))}
                </button>
              ))}

              {work_orders.length > 0 && (
                <div className="pt-2">
                  <p className="text-muted-foreground mb-2 text-xs font-medium">
                    Proposed work orders ({work_orders.length})
                  </p>
                  {work_orders.map((row) => (
                    <Link
                      key={row.id}
                      href={routes.workOrderPath(row.id)}
                      className="hover:border-primary/50 mb-2 block rounded-lg border p-3 transition-colors"
                    >
                      <span className="text-sm font-medium">
                        {row.car_mark_number ?? `WO #${row.id}`}
                      </span>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Badge variant="outline">{row.type_label}</Badge>
                        <span className="text-muted-foreground text-xs">
                          {row.summary ?? row.status_label}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {selected ? (
              <InboxReview
                key={selected.id}
                document={selected}
                attachTargets={attach_targets}
                confirmPath={routes.confirmInboxPath(selected.id)}
                rejectPath={routes.rejectInboxPath(selected.id)}
                attachPath={routes.attachInboxPath(selected.id)}
                canEdit={can_edit}
              />
            ) : (
              <Card>
                <CardContent className="text-muted-foreground py-16 text-center text-sm">
                  No documents pending — the proposed work orders on the left
                  are reviewed on their own pages.
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
